import type { ReactElement, ReactNode } from 'react';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';

export interface PageHeaderProps {
  icon: ReactElement;
  title: ReactNode;
  subtitle?: ReactNode;
  chips?: ReactNode;
  /** Right-aligned meta in the chip row (e.g. official source badge). */
  chipRowTrailing?: ReactNode;
  actions?: ReactNode;
  /** Letter or custom node inside avatar instead of icon */
  avatarContent?: ReactNode;
  backTo?: string;
  backLabel?: string;
  titleVariant?: 'h4' | 'h5';
  mb?: number;
}

export default function PageHeader({
  icon,
  title,
  subtitle,
  chips,
  chipRowTrailing,
  actions,
  avatarContent,
  backTo,
  backLabel,
  titleVariant = 'h4',
  mb = 3,
}: PageHeaderProps) {
  return (
    <Box sx={{ mb }}>
      {backTo && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <IconButton component={RouterLink} to={backTo} aria-label="返回" size="small">
            <ArrowBackIcon />
          </IconButton>
          {backLabel && (
            <Typography variant="body2" color="text.secondary">
              {backLabel}
            </Typography>
          )}
        </Stack>
      )}

      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: 3,
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.secondary.main}18 0%, ${t.palette.background.paper} 55%, ${t.palette.primary.main}08 100%)`,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          spacing={2}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: 'secondary.main',
                boxShadow: (t) => `0 8px 24px ${t.palette.secondary.main}44`,
                fontWeight: 800,
                fontSize: avatarContent ? '1.25rem' : undefined,
              }}
            >
              {avatarContent ?? icon}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant={titleVariant} fontWeight={800} gutterBottom={!!subtitle || !!chips}>
                {title}
              </Typography>
              {subtitle && (
                <Box
                  sx={{
                    color: 'text.secondary',
                    typography: titleVariant === 'h5' ? 'body2' : 'body1',
                  }}
                >
                  {subtitle}
                </Box>
              )}
              {(chips || chipRowTrailing) && (
                <Stack
                  direction="row"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mt: subtitle ? 1.25 : 0.75, width: '100%', gap: 1 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {chips && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                      {chips}
                    </Stack>
                  )}
                  {chipRowTrailing && (
                    <Box sx={{ ml: { xs: 0, sm: 'auto' }, flexShrink: 0 }}>{chipRowTrailing}</Box>
                  )}
                </Stack>
              )}
            </Box>
          </Stack>
          {actions && (
            <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
              {actions}
            </Stack>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
